import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  BookOpen,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MapPin,
  Plus,
  School,
  Star,
} from 'lucide-react';
import {
  createAdminCollege,
  fetchAdminCollege,
  fetchAdminColleges,
  patchAdminCollege,
  type AdminCollegeRow,
} from '../../api/admin';
import { ApiError } from '../../api/client';
import {
  AdminDetailCard,
  AdminEmptyState,
  AdminKpiStrip,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTablePanel,
} from '../../components/admin/AdminPageLayout';
import { LoadingState, ErrorState } from '../../components/states/StateViews';

const TYPE_LABELS: Record<string, string> = {
  central: 'Central university',
  state: 'State university',
  deemed: 'Deemed university',
  institute: 'Institute',
  private: 'Private university',
};

function typeLabel(type: string) {
  return TYPE_LABELS[type] ?? type;
}

function CreateCollegeModal({
  open,
  onClose,
  types,
  states,
}: {
  open: boolean;
  onClose: () => void;
  types: string[];
  states: string[];
}) {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: '',
    state: states[0] ?? 'Delhi',
    city: '',
    type: types[0] ?? 'state',
    courses: '',
    status: 'draft',
    website: '',
    isFeatured: false,
  });
  const [error, setError] = useState('');

  useEffect(() => {
    if (!open) return;
    setForm({
      name: '',
      state: states[0] ?? 'Delhi',
      city: '',
      type: types[0] ?? 'state',
      courses: '',
      status: 'draft',
      website: '',
      isFeatured: false,
    });
    setError('');
  }, [open, types, states]);

  const create = useMutation({
    mutationFn: () =>
      createAdminCollege({
        name: form.name.trim(),
        state: form.state.trim(),
        city: form.city.trim() || undefined,
        type: form.type,
        courses: form.courses ? Number(form.courses) : 0,
        status: form.status,
        website: form.website.trim() || undefined,
        isFeatured: form.isFeatured,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-colleges'] });
      onClose();
    },
    onError: (err) => {
      setError(err instanceof ApiError ? err.message : 'Failed to create college');
    },
  });

  if (!open) return null;

  return (
    <div className="wfx-admin-modal-backdrop" role="presentation" onClick={onClose}>
      <div
        className="wfx-admin-modal glass-surface"
        role="dialog"
        aria-labelledby="create-college-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="wfx-admin-modal-head">
          <div>
            <p className="wfx-admin-breadcrumb">College directory</p>
            <h2 id="create-college-title"><School size={20} /> New college</h2>
          </div>
        </header>
        <form
          className="wfx-admin-form"
          onSubmit={(e) => {
            e.preventDefault();
            setError('');
            create.mutate();
          }}
        >
          <label className="wfx-admin-field">
            <span>Name</span>
            <input required value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} />
          </label>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>State</span>
              <input
                required
                list="college-states"
                value={form.state}
                onChange={(e) => setForm((f) => ({ ...f, state: e.target.value }))}
              />
              <datalist id="college-states">
                {states.map((state) => <option key={state} value={state} />)}
              </datalist>
            </label>
            <label className="wfx-admin-field">
              <span>City</span>
              <input value={form.city} onChange={(e) => setForm((f) => ({ ...f, city: e.target.value }))} />
            </label>
          </div>
          <div className="wfx-admin-form-row">
            <label className="wfx-admin-field">
              <span>Type</span>
              <select value={form.type} onChange={(e) => setForm((f) => ({ ...f, type: e.target.value }))}>
                {types.map((type) => (
                  <option key={type} value={type}>{typeLabel(type)}</option>
                ))}
              </select>
            </label>
            <label className="wfx-admin-field">
              <span>Courses</span>
              <input type="number" value={form.courses} onChange={(e) => setForm((f) => ({ ...f, courses: e.target.value }))} />
            </label>
          </div>
          <label className="wfx-admin-field">
            <span>Website</span>
            <input value={form.website} placeholder="https://" onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))} />
          </label>
          <label className="wfx-admin-field">
            <span>Status</span>
            <select value={form.status} onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
              <option value="draft">Draft</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>
          </label>
          <label className="wfx-admin-checkbox">
            <input type="checkbox" checked={form.isFeatured} onChange={(e) => setForm((f) => ({ ...f, isFeatured: e.target.checked }))} />
            Feature in directory
          </label>
          {error && <p className="wfx-admin-form-error">{error}</p>}
          <div className="wfx-admin-modal-actions">
            <button type="button" className="secondary-action" onClick={onClose}>Cancel</button>
            <button type="submit" className="primary-action" disabled={create.isPending}>
              {create.isPending ? 'Creating…' : 'Add college'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function CollegeDetailCard({
  collegeId,
  types,
  onClose,
}: {
  collegeId: string | null;
  types: string[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-college', collegeId],
    queryFn: () => fetchAdminCollege(collegeId!),
    enabled: Boolean(collegeId),
  });

  const update = useMutation({
    mutationFn: (body: Partial<AdminCollegeRow>) => patchAdminCollege(collegeId!, body),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['admin-colleges'] });
      void qc.invalidateQueries({ queryKey: ['admin-college', collegeId] });
    },
  });

  const row = data?.college;

  return (
    <AdminDetailCard
      open={Boolean(collegeId)}
      onClose={onClose}
      ariaLabel="College details"
      breadcrumb="College profile"
      title={row?.name ?? 'Loading…'}
      illustration="page.college"
      subtitle={
        row ? (
          <>
            <AdminStatusBadge status={row.status} />
            {row.isFeatured && (
              <span className="wfx-admin-opp-flag">
                <Star size={14} aria-hidden="true" /> Featured
              </span>
            )}
            <span className="wfx-muted wfx-admin-subline">{typeLabel(row.type)}</span>
          </>
        ) : undefined
      }
    >
      {isLoading && <LoadingState label="Loading college…" />}
      {isError && <ErrorState message="Could not load college." onRetry={() => void refetch()} />}

      {row && (
        <>
          <dl className="wfx-admin-meta-list wfx-admin-meta-list--grid">
            <div><dt>State</dt><dd>{row.state}</dd></div>
            <div><dt>City</dt><dd>{row.city ?? '—'}</dd></div>
            <div><dt>Courses</dt><dd>{row.courses}</dd></div>
            <div><dt>Established</dt><dd>{row.establishedYear ?? '—'}</dd></div>
            <div><dt>Accreditation</dt><dd>{row.accreditation ?? '—'}</dd></div>
            <div>
              <dt>Website</dt>
              <dd>
                {row.website ? (
                  <a href={row.website} className="secondary-action compact" target="_blank" rel="noreferrer">
                    Visit site
                    <ExternalLink size={12} aria-hidden="true" />
                  </a>
                ) : '—'}
              </dd>
            </div>
          </dl>

          <div className="wfx-admin-drawer-columns">
            <div className="wfx-admin-drawer-section">
              <h3>Profile</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Name</span>
                  <input
                    defaultValue={row.name}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value && value !== row.name) update.mutate({ name: value });
                    }}
                  />
                </label>
                <div className="wfx-admin-form-row">
                  <label className="wfx-admin-field">
                    <span>State</span>
                    <input
                      defaultValue={row.state}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (value && value !== row.state) update.mutate({ state: value });
                      }}
                    />
                  </label>
                  <label className="wfx-admin-field">
                    <span>City</span>
                    <input
                      defaultValue={row.city ?? ''}
                      onBlur={(e) => {
                        const value = e.target.value.trim();
                        if (value !== (row.city ?? '')) update.mutate({ city: value || null });
                      }}
                    />
                  </label>
                </div>
                <div className="wfx-admin-form-row">
                  <label className="wfx-admin-field">
                    <span>Type</span>
                    <select
                      defaultValue={row.type}
                      onChange={(e) => update.mutate({ type: e.target.value })}
                    >
                      {types.map((type) => (
                        <option key={type} value={type}>{typeLabel(type)}</option>
                      ))}
                    </select>
                  </label>
                  <label className="wfx-admin-field">
                    <span>Status</span>
                    <select
                      defaultValue={row.status}
                      onChange={(e) => update.mutate({ status: e.target.value })}
                    >
                      <option value="draft">Draft</option>
                      <option value="published">Published</option>
                      <option value="archived">Archived</option>
                    </select>
                  </label>
                </div>
                <label className="wfx-admin-field">
                  <span>Description</span>
                  <textarea
                    rows={4}
                    className="wfx-admin-textarea"
                    defaultValue={row.description ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.description ?? '')) update.mutate({ description: value || null });
                    }}
                  />
                </label>
              </div>
            </div>

            <div className="wfx-admin-drawer-section">
              <h3>Listing details</h3>
              <div className="wfx-admin-form">
                <label className="wfx-admin-field">
                  <span>Courses offered</span>
                  <input
                    type="number"
                    defaultValue={row.courses}
                    onBlur={(e) => {
                      const value = Number(e.target.value) || 0;
                      if (value !== row.courses) update.mutate({ courses: value });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Established year</span>
                  <input
                    type="number"
                    defaultValue={row.establishedYear ?? ''}
                    onBlur={(e) => {
                      const value = e.target.value ? Number(e.target.value) : null;
                      if (value !== row.establishedYear) update.mutate({ establishedYear: value });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Accreditation</span>
                  <input
                    defaultValue={row.accreditation ?? ''}
                    placeholder="NAAC A++"
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.accreditation ?? '')) update.mutate({ accreditation: value || null });
                    }}
                  />
                </label>
                <label className="wfx-admin-field">
                  <span>Website</span>
                  <input
                    defaultValue={row.website ?? ''}
                    placeholder="https://"
                    onBlur={(e) => {
                      const value = e.target.value.trim();
                      if (value !== (row.website ?? '')) update.mutate({ website: value || null });
                    }}
                  />
                </label>
                <button
                  type="button"
                  className={`chip-pill${row.isFeatured ? ' is-active' : ''}`}
                  disabled={update.isPending}
                  onClick={() => update.mutate({ isFeatured: !row.isFeatured })}
                >
                  <Star size={14} aria-hidden="true" /> Featured college
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </AdminDetailCard>
  );
}

function CollegeCard({
  row,
  onSelect,
}: {
  row: AdminCollegeRow;
  onSelect: () => void;
}) {
  return (
    <button type="button" className="wfx-admin-college-card glass-surface" onClick={onSelect}>
      <div className="wfx-admin-result-card-head">
        <AdminStatusBadge status={row.status} />
        {row.isFeatured && <Star size={14} aria-hidden="true" className="wfx-admin-job-star" />}
      </div>
      <strong>{row.name}</strong>
      <span className="wfx-muted wfx-admin-result-org">
        <MapPin size={14} aria-hidden="true" /> {row.city ? `${row.city}, ` : ''}{row.state}
      </span>
      <div className="wfx-admin-page-card-stats">
        <span>{row.typeLabel ?? typeLabel(row.type)}</span>
        <span><BookOpen size={14} aria-hidden="true" /> {row.courses} courses</span>
      </div>
    </button>
  );
}

export function AdminCollegesScreen() {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [createOpen, setCreateOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  useEffect(() => {
    setPage(1);
  }, [debouncedQuery, stateFilter, typeFilter, statusFilter]);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['admin-colleges', debouncedQuery, stateFilter, typeFilter, statusFilter, page],
    queryFn: () =>
      fetchAdminColleges({
        q: debouncedQuery || undefined,
        state: stateFilter,
        type: typeFilter,
        status: statusFilter,
        page,
        limit: 12,
      }),
  });

  const rows = data?.data ?? [];
  const stats = data?.stats;
  const types = data?.types ?? Object.keys(TYPE_LABELS);
  const states = data?.states ?? [];
  const pagination = data?.pagination;

  const kpiItems = useMemo(
    () => [
      { label: 'Total colleges', value: stats?.total ?? 0 },
      { label: 'Published', value: stats?.published ?? 0 },
      { label: 'Draft', value: stats?.draft ?? 0 },
      { label: 'States', value: stats?.states ?? 0 },
    ],
    [stats],
  );

  if (isLoading && !data) return <LoadingState label="Loading colleges…" />;
  if (isError) return <ErrorState message="Could not load colleges." onRetry={() => void refetch()} />;

  return (
    <>
      <AdminPageLayout
        breadcrumb="Admin / Colleges"
        title="Colleges"
        description="Manage college and university profiles — location, courses, accreditation, and publication status."
        icon={School}
        illustration="page.college"
        actions={(
          <button type="button" className="primary-action compact" onClick={() => setCreateOpen(true)}>
            <Plus size={16} aria-hidden="true" />
            New college
          </button>
        )}
      >
        <AdminKpiStrip items={kpiItems} />

        <div className="wfx-admin-blog-grid">
          {rows.map((row) => (
            <CollegeCard key={row.id} row={row} onSelect={() => setSelectedId(row.id)} />
          ))}
        </div>
        {rows.length === 0 && <AdminEmptyState message="No colleges match your filters." />}

        <AdminTablePanel
          title="College directory"
          toolbar={(
            <div className="wfx-admin-toolbar">
              <AdminSearchInput
                value={query}
                onChange={setQuery}
                placeholder="Search name, state, or city…"
              />
              <select
                className="wfx-admin-select"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
                aria-label="Filter by state"
              >
                <option value="all">All states</option>
                {states.map((state) => (
                  <option key={state} value={state}>{state}</option>
                ))}
              </select>
              <select
                className="wfx-admin-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                aria-label="Filter by type"
              >
                <option value="all">All types</option>
                {types.map((type) => (
                  <option key={type} value={type}>{typeLabel(type)}</option>
                ))}
              </select>
              <select
                className="wfx-admin-select"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                aria-label="Filter by status"
              >
                <option value="all">All status</option>
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
          )}
          footer={
            pagination && pagination.pages > 1 ? (
              <div className="wfx-admin-pagination">
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => p - 1)}
                >
                  <ChevronLeft size={16} aria-hidden="true" />
                  Previous
                </button>
                <span className="wfx-muted">
                  Page {pagination.page} of {pagination.pages} · {pagination.total} colleges
                </span>
                <button
                  type="button"
                  className="secondary-action compact"
                  disabled={page >= pagination.pages}
                  onClick={() => setPage((p) => p + 1)}
                >
                  Next
                  <ChevronRight size={16} aria-hidden="true" />
                </button>
              </div>
            ) : undefined
          }
        >
          <table className="wfx-admin-table">
            <thead>
              <tr><th>Name</th><th>State</th><th>Type</th><th>Courses</th><th>Status</th><th>Featured</th></tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr
                  key={row.id}
                  className="wfx-admin-table-row-clickable"
                  onClick={() => setSelectedId(row.id)}
                >
                  <td><strong>{row.name}</strong></td>
                  <td>{row.state}</td>
                  <td>{row.typeLabel ?? typeLabel(row.type)}</td>
                  <td>{row.courses}</td>
                  <td><AdminStatusBadge status={row.status} /></td>
                  <td>{row.isFeatured ? 'Yes' : 'No'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </AdminTablePanel>
      </AdminPageLayout>

      <CreateCollegeModal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        types={types}
        states={states}
      />
      <CollegeDetailCard
        collegeId={selectedId}
        types={types}
        onClose={() => setSelectedId(null)}
      />
    </>
  );
}
